import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  LayoutDashboard, User, Download, Calculator, Shield, Code, 
  CheckCircle, AlertCircle, Mail, Plus, Loader2 
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Dashboard() {
  const { user, token, logout, resendVerification, isAuthenticated, loading: authLoading } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resendLoading, setResendLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login');
    }
  }, [authLoading, isAuthenticated, navigate]);

  useEffect(() => {
    if (token) {
      fetchStats();
    }
  }, [token]);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    setResendLoading(true);
    try {
      await resendVerification();
      toast.success('Verification email sent! Check your inbox.');
    } catch (error) {
      toast.error('Failed to send verification email');
    } finally {
      setResendLoading(false);
    }
  };

  if (authLoading || !user) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-12">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-blue-900/20 via-gray-950 to-gray-950 -z-10" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold tracking-tight mb-2 font-['Outfit']">
              Welcome, {user.name}
            </h1>
            <div className="flex items-center gap-3">
              <Badge className={user.role === 'developer' ? 'bg-green-500/20 text-green-400 border-green-500/30' : 'bg-blue-500/20 text-blue-400 border-blue-500/30'}>
                {user.role === 'developer' ? <Code className="w-3 h-3 mr-1" /> : <User className="w-3 h-3 mr-1" />}
                {user.role}
              </Badge>
              {user.email_verified ? (
                <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                  <CheckCircle className="w-3 h-3 mr-1" />
                  Verified
                </Badge>
              ) : (
                <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30">
                  <AlertCircle className="w-3 h-3 mr-1" />
                  Unverified
                </Badge>
              )}
            </div>
          </div>
          <Button variant="outline" onClick={logout} className="mt-4 md:mt-0 border-white/20">
            Sign Out
          </Button>
        </div>

        {/* Email Verification Banner */}
        {!user.email_verified && (
          <Card className="bg-yellow-500/10 border-yellow-500/30 mb-8">
            <CardContent className="p-4 flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-yellow-400" />
                <div>
                  <p className="text-yellow-400 font-medium">Verify your email</p>
                  <p className="text-gray-400 text-sm">Check your inbox for a verification link</p>
                </div>
              </div>
              <Button
                onClick={handleResendVerification}
                disabled={resendLoading}
                variant="outline"
                className="border-yellow-500/30 text-yellow-400 hover:bg-yellow-500/10"
                data-testid="resend-verification-btn"
              >
                {resendLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Resend Email'}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {user.role === 'developer' ? (
            <>
              <Card className="bg-gray-900/50 border-white/10">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-gray-400 text-sm">Your Software</p>
                      <p className="text-3xl font-bold text-white">{stats?.software_count || 0}</p>
                    </div>
                    <Code className="w-8 h-8 text-green-400" />
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-gray-900/50 border-white/10">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-gray-400 text-sm">Total Downloads</p>
                      <p className="text-3xl font-bold text-white">{stats?.total_downloads?.toLocaleString() || 0}</p>
                    </div>
                    <Download className="w-8 h-8 text-blue-400" />
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-gray-900/50 border-white/10">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-gray-400 text-sm">Status</p>
                      <p className="text-lg font-bold text-green-400">Active</p>
                    </div>
                    <CheckCircle className="w-8 h-8 text-green-400" />
                  </div>
                </CardContent>
              </Card>
            </>
          ) : (
            <>
              <Card className="bg-gray-900/50 border-white/10">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-gray-400 text-sm">Project Estimates</p>
                      <p className="text-3xl font-bold text-white">{stats?.estimates_count || 0}</p>
                    </div>
                    <Calculator className="w-8 h-8 text-blue-400" />
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-gray-900/50 border-white/10">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-gray-400 text-sm">File Scans</p>
                      <p className="text-3xl font-bold text-white">{stats?.scans_count || 0}</p>
                    </div>
                    <Shield className="w-8 h-8 text-green-400" />
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-gray-900/50 border-white/10">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-gray-400 text-sm">Account Status</p>
                      <p className="text-lg font-bold text-green-400">Active</p>
                    </div>
                    <CheckCircle className="w-8 h-8 text-green-400" />
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </div>

        {/* Quick Actions */}
        <Card className="bg-gray-900/50 border-white/10">
          <CardHeader>
            <CardTitle className="font-['Outfit']">Quick Actions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <Link to="/estimator">
                <Button variant="outline" className="w-full h-auto py-4 flex-col border-white/10 hover:border-blue-500/30">
                  <Calculator className="w-6 h-6 mb-2 text-blue-400" />
                  <span>Get Estimate</span>
                </Button>
              </Link>
              <Link to="/scanner">
                <Button variant="outline" className="w-full h-auto py-4 flex-col border-white/10 hover:border-green-500/30">
                  <Shield className="w-6 h-6 mb-2 text-green-400" />
                  <span>Scan File</span>
                </Button>
              </Link>
              <Link to="/software">
                <Button variant="outline" className="w-full h-auto py-4 flex-col border-white/10 hover:border-purple-500/30">
                  <Download className="w-6 h-6 mb-2 text-purple-400" />
                  <span>Free Software</span>
                </Button>
              </Link>
              <Link to="/security">
                <Button variant="outline" className="w-full h-auto py-4 flex-col border-white/10 hover:border-orange-500/30">
                  <Shield className="w-6 h-6 mb-2 text-orange-400" />
                  <span>Security Services</span>
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>

        {/* Developer Section */}
        {user.role === 'developer' && (
          <Card className="bg-gray-900/50 border-white/10 mt-6">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="font-['Outfit']">Your Software</CardTitle>
              <Button className="bg-green-500 hover:bg-green-600 text-black" size="sm">
                <Plus className="w-4 h-4 mr-1" />
                Add Software
              </Button>
            </CardHeader>
            <CardContent>
              {stats?.recent_software?.length > 0 ? (
                <div className="space-y-4">
                  {stats.recent_software.map((software) => (
                    <div key={software.id} className="flex items-center justify-between p-4 rounded-lg bg-gray-800/30">
                      <div>
                        <p className="text-white font-medium">{software.title}</p>
                        <p className="text-gray-500 text-sm">{software.downloads} downloads</p>
                      </div>
                      <Badge variant="outline" className="capitalize">{software.category}</Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Code className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                  <p className="text-gray-400">No software uploaded yet</p>
                  <p className="text-gray-500 text-sm">Share your creations with the community</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
